/**
 * Content. CARDS and SOLO originate verbatim from the v1 artifact (needle.html).
 * The Gate A game-design review cut eight spectrum pairs, reworded one, added
 * six replacements, and gave every solo challenge an authored rationale; the
 * owner then restored seven of the eight, and a second batch of 98 topics was
 * added to the same criteria. See ROUNDTABLE.md for the argument on both sides.
 *
 * 225 spectrum pairs, 40 authored solo challenges.
 * Nothing here is trademarked or copied from any published game.
 */

/** A spectrum card: [leftPole, rightPole]. Both poles are public information. */
export type Card = [string, string];

/** A solo challenge: [leftPole, rightPole, clue, target0to100, why]. */
export type SoloCard = [string, string, string, number, string];

export const CARDS: readonly Card[] = [
  ["Underrated", "Overrated"],
  ["Cold", "Hot"],
  ["Cheap", "Expensive"],
  ["Ugly", "Beautiful"],
  ["Boring", "Exciting"],
  ["Useless", "Essential"],
  ["Dirty", "Clean"],
  ["Quiet", "Loud"],
  ["Weak", "Strong"],
  ["Rare", "Common"],
  ["Chore", "Treat"],
  ["Guilty pleasure", "Proud of it"],
  ["Fake", "Real"],
  ["Casual", "Formal"],
  ["Kid's food", "Adult food"],
  ["Bad habit", "Good habit"],
  ["Waste of money", "Worth every penny"],
  ["Round", "Pointy"],
  ["Forgettable", "Unforgettable"],
  ["Soft", "Hard"],
  ["Wet", "Dry"],
  ["Slow", "Fast"],
  ["Old-fashioned", "Modern"],
  ["Simple", "Complicated"],
  ["Safe", "Dangerous"],
  ["Sad song", "Happy song"],
  ["Introvert", "Extrovert"],
  ["Snack", "Meal"],
  ["Warm color", "Cool color"],
  ["Smells bad", "Smells good"],
  ["Basic", "Fancy"],
  ["Rude", "Polite"],
  ["Terrible movie", "Great movie"],
  ["Peaceful", "Chaotic"],
  ["Toy", "Tool"],
  ["Nobody knows it", "Everybody knows it"],
  ["Temporary", "Permanent"],
  ["Lowbrow", "Highbrow"],
  ["Bad gift", "Good gift"],
  ["Small talk", "Deep conversation"],
  ["Villain", "Hero"],
  ["Vegetable", "Candy"],
  ["Job", "Passion"],
  ["Weird", "Normal"],
  ["Indoors", "Outdoors"],
  ["Whisper", "Scream"],
  ["Fails often", "Always works"],
  ["Trend", "Classic"],
  ["Bad first date", "Great first date"],
  ["Cursed", "Blessed"],
  ["Tastes bad, good for you", "Tastes good, bad for you"],
  ["Alone", "Crowded"],
  ["Manmade", "Natural"],
  ["Beginner", "Expert"],
  ["Cheap thrill", "Life-changing"],
  ["Slightly annoying", "Infuriating"],
  ["Fits in a pocket", "Needs a truck"],
  ["Public", "Private"],
  ["Dumb question", "Smart question"],
  ["Morning", "Night"],
  ["Emergency", "Whenever"],
  ["Ordinary object", "Sacred object"],
  ["Instinct", "Skill"],
  ["Sober idea", "3am idea"],
  ["Boring but safe", "Risky but fun"],
  ["Easy to ruin", "Hard to ruin"],
  ["Not a sport", "Definitely a sport"],
  ["Trash", "Treasure"],
  ["Hate to admit it", "Love to admit it"],
  ["Ancient", "Futuristic"],
  ["City", "Countryside"],
  ["Optional", "Mandatory"],
  ["Awkward", "Smooth"],
  ["Feels illegal", "Feels wholesome"],
  ["Sour", "Sweet"],
  ["Wasted talent", "Perfect fit"],
  ["Bad tattoo", "Great tattoo"],
  ["Loser move", "Power move"],
  ["Underdressed", "Overdressed"],
  ["Cringe", "Cool"],
  ["Solvable", "Hopeless"],
  ["Cheap material", "Luxury material"],
  ["I could take it", "It would destroy me"],
  ["Bad name for a dog", "Great name for a dog"],
  ["Hard to spell", "Easy to spell"],
  ["Suspicious", "Trustworthy"],
  ["Ignore it", "Call a professional"],
  ["Small problem", "Huge problem"],
  ["Nobody would notice", "Everybody would notice"],
  ["Feels like work", "Feels like play"],
  ["Bad on a résumé", "Great on a résumé"],
  ["Uncomfortable", "Cozy"],
  ["Never on time", "Always on time"],
  ["Bad pizza topping", "Great pizza topping"],
  ["Cheap to fix", "Expensive to fix"],
  ["Nobody would care", "Would go viral"],
  ["Bland", "Overpowering"],
  ["Not scary", "Terrifying"],
  ["Loose", "Tight"],
  ["Bad excuse", "Airtight excuse"],
  ["Never again", "Would have it every day"],
  ["Fake friendly", "Truly kind"],
  ["Flat", "Steep"],
  ["Aged badly", "Aged well"],
  ["Costume", "Outfit"],
  ["Bad advice", "Good advice"],
  ["Overpaid", "Underpaid"],
  ["Junk drawer", "Display shelf"],
  ["Bad haircut", "Great haircut"],
  ["Skippable", "Unmissable"],
  ["Would lend it out", "Would never lend it out"],
  ["Minor inconvenience", "Ruins the whole day"],
  ["Forgivable", "Unforgivable"],
  ["Overthought", "Winged it"],
  ["Nobody's fault", "Somebody's fault"],
  ["Fine to eat cold", "Must be hot"],
  ["Would tell a stranger", "Would tell nobody"],
  ["Should be free", "Should cost more"],
  ["Bad reason to be late", "Good reason to be late"],
  ["Tactful", "Blunt"],

  // Cut by the Gate A design review and restored at the owner's call. The
  // panel's objections are recorded in ROUNDTABLE.md; the deck is his.
  ["Illegal", "Legal"],
  ["Hard to say", "Easy to say"],
  ["Common sense", "Learned the hard way"],
  ["Free", "Priceless"],
  ["Overhyped", "Delivers"],
  ["Would survive it", "Would not survive it"],
  ["Fits everyone", "Fits nobody"],

  // A second batch of topics, added at the owner's request and held to the
  // same criteria the Gate A design review used: one continuum, no knowledge
  // gate, accepts an arbitrary noun, a usable middle, no negated pole.
  ["Slippery", "Grippy"],
  ["Light", "Heavy"],
  ["Bright", "Dim"],
  ["Crunchy", "Mushy"],
  ["Fragile", "Indestructible"],
  ["Blurry", "Crisp"],
  ["Prickly", "Fluffy"],
  ["Transparent", "Opaque"],
  ["Chewy", "Melts in your mouth"],
  ["Mild", "Fiery"],
  ["Thin", "Thick"],
  ["Still", "Fizzy"],
  ["Breakfast food", "Dinner food"],
  ["Side dish", "Main event"],
  ["Everyday drink", "Special occasion drink"],
  ["Eat it with your hands", "Needs a fork"],
  ["Homemade", "Store-bought"],
  ["Better the next day", "Only good fresh"],
  ["Acquired taste", "Instant hit"],
  ["Made for sharing", "Yours alone"],
  ["Text it", "Say it in person"],
  ["Fine to be late for", "Be early for"],
  ["Small favor", "Big favor"],
  ["Keep it private", "Post it everywhere"],
  ["Handshake", "Hug"],
  ["Fine to do alone", "Needs a friend"],
  ["Compliment", "Insult"],
  ["Bad houseguest", "Perfect houseguest"],
  ["Fine to bring kids", "Adults only"],
  ["Would return it", "Would keep it"],
  ["Fine to cancel on", "Never cancel on"],
  ["Casual acquaintance", "Best friend"],
  ["Would lend to a stranger", "Family only"],
  ["Bad roommate habit", "Great roommate habit"],
  ["Fine in public", "Home only"],
  ["Bad first impression", "Great first impression"],
  ["Rent it", "Buy it"],
  ["Free trial", "Worth subscribing"],
  ["Would haggle", "Pay the sticker price"],
  ["Loses value", "Gains value"],
  ["Impulse buy", "Researched for weeks"],
  ["Would do it for free", "Would need paying"],
  ["Not worth the line", "Worth any line"],
  ["Five minutes", "All weekend"],
  ["Can wait", "Do it today"],
  ["Sprint", "Marathon"],
  ["One-time thing", "Every single day"],
  ["Quick fix", "Real solution"],
  ["Comes with instructions", "Figure it out"],
  ["Entry level", "Takes a lifetime"],
  ["Improves with practice", "You either have it or you don't"],
  ["Tacky", "Elegant"],
  ["Minimal", "Maximal"],
  ["Beige", "Neon"],
  ["Loud pattern", "Plain"],
  ["Disposable", "Heirloom"],
  ["Would fix it", "Would replace it"],
  ["Would wear to work", "Would wear to bed"],
  ["Overpacked", "Traveling light"],
  ["Hobby", "Career"],
  ["Nobody's job", "Everybody's job"],
  ["Delegate it", "Do it yourself"],
  ["Bad thing to be famous for", "Great thing to be famous for"],
  ["Easy to fake", "Impossible to fake"],
  ["Would swim in it", "Look at it only"],
  ["Nice to visit", "Nice to live in"],
  ["Bad weather", "Perfect weather"],
  ["Tourist trap", "Hidden gem"],
  ["Would take to a desert island", "Would leave behind"],
  ["Best in summer", "Best in winter"],
  ["Mildly funny", "Can't breathe"],
  ["A little sad", "Devastating"],
  ["Warm feeling", "Chills"],
  ["Slightly nervous", "Full panic"],
  ["Vaguely annoying", "Would end a friendship"],
  ["Nothing to lose", "Everything to lose"],
  ["Forget it in a week", "Think about it for years"],
  ["Bad hill to die on", "Worth the argument"],
  ["Gut feeling", "Hard evidence"],
  ["Superstition", "Science"],
  ["Would believe a stranger", "Need proof"],
  ["Excuse", "Reason"],
  ["Would trust a robot with it", "Humans only"],
  ["Bad name for a band", "Great name for a band"],
  ["Bad superpower", "Amazing superpower"],
  ["Would ruin the wedding", "Would make the wedding"],
  ["Bad karaoke song", "Great karaoke song"],
  ["Bad email sign-off", "Great email sign-off"],
  ["Bad movie idea", "Great movie idea"],
  ["Bad podcast topic", "Great podcast topic"],
  ["Bad Halloween costume", "Great Halloween costume"],
  ["Bad group chat name", "Great group chat name"],
  ["Bad bumper sticker", "Great bumper sticker"],
  ["Bad souvenir", "Great souvenir"],
  ["Bad wedding song", "Great wedding song"],
  ["Bad pet", "Great pet"],
  ["Would delete", "Would keep forever"],
  ["Would put in a time capsule", "Would leave out"],
];

/**
 * The Cape Town deck — the "Cape Town mode" on the front door.
 *
 * Same rules as the main deck (one continuum, a usable middle, no negated
 * poles) with one criterion deliberately dropped: these cards DO assume local
 * knowledge. That is the whole point — the gate the main deck avoids is the
 * feature here. Weighted to the Deep South: Noordhoek, Kommetjie, Muizenberg,
 * the surf, and the small stuff you only know if you live there.
 */
export const CAPE_TOWN_CARDS: readonly Card[] = [
  ["Kook", "Local"],
  ["Knee-high", "Double overhead"],
  ["Onshore mess", "Glassy"],
  ["Muizenberg", "Dungeons"],
  ["Longboard wave", "Shortboard wave"],
  ["Paddle out alone", "Fifty guys on the peak"],
  ["Summer suit", "Booties, hood and gloves"],
  ["Beginner's beach break", "Slab over dry reef"],
  ["Foamie", "Step-up gun"],
  ["Inner Kom", "Outer Kom"],
  ["Sharky", "Safe as houses"],
  ["Flat for a week", "Swell of the year"],
  ["Rented board", "Custom shape"],
  ["Surf check from the car", "Already paddling at first light"],
  ["Rip you can swim out of", "Rip that takes you to Robben Island"],
  ["Muizenberg on a Sunday", "Misty Cliffs on a weekday"],
  ["Cape Doctor howling", "Not a breath of wind"],
  ["Shark spotters' green flag", "Shark spotters' white flag"],
  ["Kalky's", "La Colombe"],
  ["Toad in the Village", "The Foodbarn"],
  ["Gatsby", "Tasting menu"],
  ["Snoek on the braai", "Sushi platter"],
  ["Woolies", "Shoprite"],
  ["Checkers Sixty60", "Walk to the Spar"],
  ["Rooibos", "Double espresso"],
  ["Boerewors roll", "Wagyu burger"],
  ["Milk tart", "Crème brûlée"],
  ["Braai", "White tablecloth"],
  ["Klipdrift and Coke", "Wine farm tasting"],
  ["Padkos in a tupperware", "Picnic at Cape Point Vineyards"],
  ["Biltong", "Charcuterie board"],
  ["Walk-in any night", "Book three weeks ahead"],
  ["Ou Kaapse Weg", "Chapman's Peak Drive"],
  ["Sea Point promenade", "Table Mountain summit"],
  ["Kalk Bay", "Camps Bay"],
  ["Noordhoek", "Long Street"],
  ["Silvermine", "Lion’s Head at full moon"],
  ["Five-minute drive", "Stuck on the M3"],
  ["Boulders penguins", "Cape Point baboons"],
  ["Walk to the beach", "Need a 4x4 to get there"],
  ["Wind-blasted", "Perfectly sheltered"],
  ["Tourist bus stop", "Only locals know it"],
  ["Worth the toll", "Take the long way round"],
  ["Ten minutes on Long Beach", "All the way to Kommetjie"],
  ["Noordhoek Farm Village", "Canal Walk"],
  ["Just now", "Now now"],
  ["Load-shedding stage 1", "Stage 6"],
  ["EskomSePush notification", "Generator already running"],
  ["Table Mountain tablecloth", "Clear blue"],
  ["Baboon-proof", "Baboons will get in"],
  ["Slip slops", "Black tie"],
  ["Free parkrun", "Two Oceans entry fee"],
  ["Sunrise at Muizenberg", "Sundowners at Camps Bay"],
  ["A week of winter rain", "Windless autumn day"],
  ["Beach horses at sunrise", "Jet ski at noon"],
  ["Kakapo wreck", "V&A Waterfront"],
  ["Fynbos", "Manicured lawn"],
  ["Bakkie on the dirt road", "Detailed car at the mall"],
  ["Everyone knows your dog", "Nobody knows your name"],
  ["Power cut braai", "Candlelit dinner"],
  ["One robot the whole way home", "Every robot red"],
];

/** Which spectrum deck a game is played with. */
export type DeckId = 'normal' | 'capetown';

export const DECKS: Record<DeckId, { label: string; blurb: string; cards: readonly Card[] }> = {
  normal: {
    label: 'Normal',
    blurb: 'The full deck — anything goes.',
    cards: CARDS,
  },
  capetown: {
    label: 'Cape Town',
    blurb: 'Local knowledge required. The surf, the Deep South, and load-shedding.',
    cards: CAPE_TOWN_CARDS,
  },
};

export function deckFor(id: DeckId): readonly Card[] {
  return (DECKS[id] ?? DECKS.normal).cards;
}

/**
 * Solo challenges: someone else's clue, their intended spot, and — at reveal —
 * why they meant it. Disagreeing with a solo answer is the game working; the
 * rationale is the absent author arguing back.
 */
export const SOLO: readonly SoloCard[] = [
  ["Cold", "Hot", "A swimming pool in May", 32, "Swimmable if you commit. Nobody would call it warm."],
  ["Underrated", "Overrated", "Black Friday", 84, "Not worthless — just wildly oversold relative to the queue."],
  ["Useless", "Essential", "A can opener", 62, "You need it about twice a month, and nothing else will do."],
  ["Chore", "Treat", "Mowing the lawn", 22, "A chore, but one with a visible result and a smell people like."],
  ["Quiet", "Loud", "A library", 10, "Not silent — pages, chairs, someone whispering badly."],
  ["Cheap", "Expensive", "A wedding", 90, "Not the most expensive thing in a life, but close."],
  ["Snack", "Meal", "A bowl of cereal", 38, "It can be dinner. It is rarely trying to be."],
  ["Fake", "Real", "A movie based on a true story", 55, "The bones are real, the dialogue never is."],
  ["Weird", "Normal", "Talking to your pet", 72, "Almost everyone does it. Almost nobody does it in public."],
  ["Bad gift", "Good gift", "A gift card", 48, "Useful and thoughtless in exactly equal measure."],
  ["Casual", "Formal", "A polo shirt", 40, "It is what you wear when you were told the dress code is unclear."],
  ["Not a sport", "Definitely a sport", "Competitive eating", 30, "Trained, scored, televised — and no athletic movement in it."],
  ["Trash", "Treasure", "A shoebox of old photos", 88, "Worthless to everyone but one person, who would run into a fire for it."],
  ["Slow", "Fast", "A city bus", 30, "Faster than walking, slower than anything with its own lane."],
  ["Simple", "Complicated", "Doing your taxes", 90, "Simple in principle, and nobody has ever felt that way in April."],
  ["Safe", "Dangerous", "A trampoline", 62, "Fine alone. The danger arrives with the second child."],
  ["Bad habit", "Good habit", "Checking your phone in bed", 12, "Almost nothing to defend, and almost everyone does it."],
  ["Waste of money", "Worth every penny", "A good mattress", 92, "A third of your life, amortized over a decade."],
  ["Boring", "Exciting", "Watching paint dry", 4, "The reference case for boredom. Not quite zero — you could be watching a wall."],
  ["Morning", "Night", "Pancakes", 15, "Breakfast food, but people do make them at midnight."],
  ["Kid's food", "Adult food", "Mac and cheese", 22, "It grows up a little with pepper and a good cheese, but only a little."],
  ["Indoors", "Outdoors", "A greenhouse", 55, "Roofed, walled, and full of weather. Dead centre with a lean outside."],
  ["Nobody knows it", "Everybody knows it", "Happy Birthday", 97, "Possibly the most widely known melody there is."],
  ["Rude", "Polite", "Replying just 'k'", 15, "Not an insult. Somehow worse than one."],
  ["Ancient", "Futuristic", "A fax machine", 38, "Obsolete is not ancient — it is the recent past, sitting just behind us."],
  ["Cringe", "Cool", "Matching family pajamas", 25, "Cringe to everyone outside the photograph, beloved inside it."],
  ["Peaceful", "Chaotic", "A toddler's birthday party", 88, "Sugar, no schedule, and one child who did not want to come."],
  ["Soft", "Hard", "A ripe peach", 25, "Soft, but there is a stone in the middle of it."],
  ["Awkward", "Smooth", "Hugging someone going for a handshake", 5, "The canonical case. The arm is already out."],
  ["Loser move", "Power move", "Leaving a party without saying goodbye", 68, "Rude in theory. Enviable in practice."],
  ["Optional", "Mandatory", "Tipping in an American restaurant", 72, "Technically optional. Socially, not remotely."],
  ["Small problem", "Huge problem", "A flat tire", 45, "Ruins an afternoon, costs a little money, ends the same day."],
  ["Instinct", "Skill", "Parallel parking", 75, "Almost entirely learned, and everyone insists otherwise."],
  ["Sour", "Sweet", "Ketchup", 58, "People forget how much sugar is in it until they read the label."],
  ["Aged badly", "Aged well", "Denim jackets", 78, "Sixty years in and still not embarrassing."],
  ["Bad pizza topping", "Great pizza topping", "Pineapple", 50, "Sweet and hot and acidic all at once — it is either the best idea on the menu or an outrage, and it is genuinely both."],
  ["Nobody would notice", "Everybody would notice", "Your friend got a haircut", 52, "Half the room clocks it instantly, half never will."],
  ["Feels like work", "Feels like play", "Cooking dinner for friends", 68, "It is work. It does not feel like it until the washing up."],
  ["Bad excuse", "Airtight excuse", "My flight got cancelled", 85, "Verifiable, blameless, and it happens constantly."],
  ["Junk drawer", "Display shelf", "Your high school trophies", 42, "You will not throw them out, and they are not going on a shelf either."],
];

/** How many of the 40 solo challenges are dealt in one solo run. */
export const SOLO_ROUNDS = 10;

/**
 * Clues that make a good first impression: mid-band targets, unambiguous axes.
 * One of these is always dealt first so nobody's first-ever round teaches them
 * that the ends of the dial are where the game lives.
 */
export const SOLO_OPENERS: readonly string[] = [
  "A gift card",
  "A greenhouse",
  "A bowl of cereal",
];
